import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Chip
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import useLeads from "../../hooks/useLeads";
import LeadModal from "../../components/LeadModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import NewTicketModal from "../../components/NewTicketModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.type === "light" ? "#f5f5f5" : theme.palette.background.default,
    padding: theme.spacing(4),
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  addButton: {
    borderRadius: "50px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: theme.shadows[3],
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
  paperTable: {
    flex: 1,
    overflowY: "auto",
    width: "100%",
    padding: theme.spacing(2),
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "16px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
    border: "1px solid",
    borderColor: theme.palette.divider,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  leadNameTitle: {
    fontWeight: "bold",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  infoBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
}));

const Leads = () => {
  const classes = useStyles();
  const history = useHistory();
  const { findAll, deleteLead } = useLeads();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingLead, setDeletingLead] = useState(null);
  
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const data = await findAll();
      if (data?.leads) {
        setLeads(data.leads);
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleOpenLeadModal = () => {
    setSelectedLead(null);
    setModalOpen(true);
  };

  const handleCloseLeadModal = () => {
    setModalOpen(false);
    setSelectedLead(null);
    loadLeads();
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setModalOpen(true);
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await deleteLead(leadId);
      toast.success("Lead excluído com sucesso");
      loadLeads();
    } catch (err) {
      toastError(err);
    }
    setDeletingLead(null);
  };

  return (
    <div className={classes.mainContainer}>
      <LeadModal
        open={modalOpen}
        onClose={handleCloseLeadModal}
        lead={selectedLead}
      />
      <ConfirmationModal
        title={`Excluir Lead ${deletingLead?.name}?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDeleteLead(deletingLead?.id)}
      >
        Você tem certeza que deseja excluir este Lead? Esta ação não pode ser desfeita.
      </ConfirmationModal>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          setNewTicketModalOpen(false);
          if (ticket && ticket.uuid) {
            history.push(`/tickets/${ticket.uuid}`);
          }
        }}
      />

      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>
            Leads ({leads.length})
          </Typography>
          <Typography className={classes.subtitle}>
            Gerenciamento de Captações
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenLeadModal}
          className={classes.addButton}
          startIcon={<AddIcon />}
        >
          Adicionar Lead
        </Button>
      </div>

      <Paper className={classes.paperTable} variant="outlined">
        <Grid container spacing={3}>
          {leads.map((lead) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={lead.id}>
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <Box mb={2}>
                    <Typography variant="h6" className={classes.leadNameTitle} title={lead.name}>
                      {lead.name}
                    </Typography>
                    {lead.source && (
                      <Chip
                        label={lead.source}
                        size="small"
                        color="primary"
                        variant="outlined"
                        style={{ marginTop: 4 }}
                      />
                    )}
                  </Box>

                  <Box className={classes.infoBox}>
                    <Typography variant="body2" color="textSecondary" noWrap title={lead.email}>
                      <strong>Email:</strong> {lead.email || "Não informado"}
                    </Typography>
                  </Box>

                  <Box className={classes.infoBox}>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      <strong>Valor:</strong> {lead.dealValue ? `R$ ${lead.dealValue}` : "Não informado"}
                    </Typography>
                  </Box>

                  <Box className={classes.infoBox}>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      <strong>Entrada:</strong> {lead.entryDate ? format(parseISO(lead.entryDate), "dd/MM/yyyy") : "-"}
                    </Typography>
                  </Box>
                </CardContent>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    bgcolor: "background.default",
                  }}
                  style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: "#fafafa" }}
                >
                  <Box>
                    {lead.contactId && lead.contact && (
                      <Tooltip title="Iniciar Atendimento (WhatsApp)" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setContactTicket(lead.contact);
                              setNewTicketModalOpen(true);
                            }}
                            style={{ color: "green" }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Box>
                    <Tooltip title="Editar" arrow>
                      <IconButton size="small" color="primary" onClick={() => handleEditLead(lead)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir" arrow>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeletingLead(lead);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
          {leads.length === 0 && !loading && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <Typography variant="body1" color="textSecondary">
                  Nenhum Lead encontrado.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </div>
  );
};

export default Leads;
